import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Card, FormField, Input, Stack, guardSubmit } from "@salesapp/ui";
import { orgSettingsApi } from "../lib/orgSettingsApi";
import { ApiError } from "../lib/api";
import "./pages.css";
import { ThemeToggle } from "../shell/ThemeToggle";

export const RESET_ACCEPTED =
  "If that email has a password sign-in, we sent a setup link. It expires in 15 minutes and can only be used once.";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") ?? "");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await orgSettingsApi.requestPasswordReset(email);
      setMessage(RESET_ACCEPTED);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(err.message);
      } else if (err instanceof ApiError) {
        setMessage(RESET_ACCEPTED);
      } else {
        setError("Unable to send a reset link due to a network issue.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page-frame">
      <ThemeToggle />
      <Card padding="lg" className="login-card">
        <Stack gap={5}>
          <Stack gap={2} align="center">
            <img src="/logo-mark.svg" alt="" className="login-brand-mark" />
            <h1 className="login-brand-title">Reset your password</h1>
            <p className="login-brand-subtitle">
              Enter the email you use to sign in. This only works for password accounts, not SSO.
            </p>
          </Stack>
          {message ? (
            <Stack gap={4}>
              <p role="status" className="login-status">{message}</p>
              <p className="login-brand-subtitle">
                <Link to="/login">Back to sign in</Link>
              </p>
            </Stack>
          ) : (
            <Stack as="form" gap={4} onSubmit={guardSubmit(onSubmit)}>
              {error && (
                <p role="alert" className="settings-error">{error}</p>
              )}
              <FormField label="Email" htmlFor="reset-email" required>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </FormField>
              <Button type="submit" fullWidth disabled={submitting || !email}>
                {submitting ? "Sending..." : "Send setup link"}
              </Button>
              <p className="login-brand-subtitle">
                <Link to="/login">Back to sign in</Link>
              </p>
            </Stack>
          )}
        </Stack>
      </Card>
    </div>
  );
}
